import { FC, useCallback } from 'react';
import { api } from 'services/api';
import { TPgRefund } from 'services/api/paymentGateway/types';
import { DATE_FILTER } from 'ui/EasTable/constants';
import { EIconButtons } from 'ui/EasTable/EasTableRow/EasTableValue/constants';
import {
	ESortDirection,
	ETableStructureAlign,
	ETableStructureType,
	TTableStructure,
} from 'ui/EasTable/types';
import { notificationContainer } from 'utils/notificationContainer';
import { getPgErrorMessage } from 'services/api/paymentGateway/errors';
import { PgList } from '../common/PgList';
import { useReloadKey } from '../common/helpers';
import { pgActionsColumn, pgColumn, statusColumn } from '../common/tableHelpers';
import { formatMoney } from '../utils';
import { usePaymentViewer } from './PaymentViewer';

type TRow = TPgRefund;
const col = (
	name: string,
	selector: keyof TRow & string,
	type: ETableStructureType,
	extra: Partial<TTableStructure<keyof TRow, TRow>> = {},
) => pgColumn<TRow>(name, selector, type, extra);

const OPEN_PAYMENT = 'Payment';
const SYNC = 'Sync';

const STRUCTURE: TTableStructure<keyof TRow, TRow>[] = [
	col('Created', 'createdAt', ETableStructureType.DATE_TIME, {
		sortable: true,
		filterable: true,
		filter: DATE_FILTER,
	}),
	col('Status', 'status', ETableStructureType.STATUS, {
		sortable: true,
		filterable: true,
		...statusColumn([
			'created',
			'requested',
			'received',
			'accepted',
			'settled',
			'failed',
			'unknown',
		]),
	}),
	col('Client', 'clientNumber', ETableStructureType.STRING, { sortable: true, filterable: true }),
	col('Invoice', 'invoiceNumber', ETableStructureType.STRING, {
		sortable: true,
		value: (item) => (item.invoiceNumber === null ? '' : String(item.invoiceNumber)),
	}),
	col('Amount', 'amount', ETableStructureType.STRING, {
		sortable: true,
		align: ETableStructureAlign.RIGHT,
		value: (item) => formatMoney(item.amount, item.currency),
	}),
	col('EAS adjustment', 'easAdjustmentEur', ETableStructureType.STRING, {
		align: ETableStructureAlign.RIGHT,
		value: (item) => formatMoney(item.easAdjustmentEur, 'EUR'),
	}),
	col('EAS sync', 'easSyncStatus', ETableStructureType.STATUS, {
		sortable: true,
		...statusColumn(['not_required', 'pending', 'done', 'failed', 'conflict']),
	}),
	col('Reason', 'reason', ETableStructureType.STRING, { tooltip: (item) => item.reason }),
	col('By', 'createdByName', ETableStructureType.STRING, {
		value: (item) => item.createdByName ?? item.createdBySub,
	}),
	col('Failure', 'failureMessage', ETableStructureType.STRING, {
		tooltip: (item) => item.failureMessage ?? '',
	}),
	col('Completed', 'completedAt', ETableStructureType.DATE_TIME, { sortable: true }),
	pgActionsColumn<TRow>([
		{ name: OPEN_PAYMENT, label: 'Open the payment', icon: EIconButtons.SUMMARIZE },
		{ name: SYNC, label: 'Re-read from Airwallex', icon: EIconButtons.HISTORY },
	]),
];

type TRefundsListProps = {
	reloadKey: number;
};

/** Refunds of all payments (03 §4.9); "Sync" re-reads one refund from Airwallex. */
export const RefundsList: FC<TRefundsListProps> = ({ reloadKey }) => {
	const { openPayment, changeCounter } = usePaymentViewer();
	const [localKey, setLocalKey] = useReloadKey();

	const handleAction = useCallback(
		(row: TRow, action: string) => {
			if (action === OPEN_PAYMENT) {
				openPayment(row.paymentTransactionId);
				return;
			}
			if (action !== SYNC) return;
			api.paymentGateway
				.syncRefund(row.id)
				.then(() => {
					notificationContainer('Refund re-read from Airwallex', 'success');
					setLocalKey();
				})
				.catch((e) => notificationContainer(getPgErrorMessage(e, 'Sync failed'), 'error'));
		},
		[openPayment, setLocalKey],
	);

	return (
		<PgList<TRow>
			title="Refunds"
			structure={STRUCTURE}
			fetcher={api.paymentGateway.getRefunds}
			onAction={handleAction}
			defaultOrder={{ fieldName: 'createdAt', direction: ESortDirection.DESC }}
			reloadKey={reloadKey + changeCounter + localKey}
			errorText="Failed to load refunds"
		/>
	);
};
